import { IconChevronDown } from '@tabler/icons-react';

import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import { Menu, UnstyledButton } from '@mantine/core';

import { useEditorAdapter } from '../core/EditorAdapterContext';
import type { BlockTypeValue } from '../core/types';

interface BlockTypeOption {
    value: BlockTypeValue;
    label: string;
    icon: ReactNode;
}

const iconStyle: CSSProperties = {
    display: 'inline-block',
    width: '20px',
    textAlign: 'center'
};

const BLOCK_TYPES: BlockTypeOption[] = [
    { value: 'paragraph', label: 'Paragraph', icon: <span style={iconStyle}>P</span> },
    { value: 'h1', label: 'Heading 1', icon: <strong style={iconStyle}>H1</strong> },
    { value: 'h2', label: 'Heading 2', icon: <strong style={iconStyle}>H2</strong> },
    { value: 'h3', label: 'Heading 3', icon: <strong style={iconStyle}>H3</strong> },
    { value: 'h4', label: 'Heading 4', icon: <strong style={iconStyle}>H4</strong> },
    { value: 'h5', label: 'Heading 5', icon: <strong style={iconStyle}>H5</strong> },
    { value: 'h6', label: 'Heading 6', icon: <strong style={iconStyle}>H6</strong> },
    { value: 'quote', label: 'Quote', icon: <span style={iconStyle}>&ldquo;</span> },
    { value: 'code', label: 'Code Block', icon: <span style={iconStyle}>&lt;/&gt;</span> }
];

const triggerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 4px 2px 8px',
    borderRadius: 'var(--radius-medium, 4px)',
    backgroundColor: 'var(--basePageBg)',
    color: 'inherit',
    fontSize: 'var(--text-sm, 13px)',
    whiteSpace: 'nowrap',
    margin: '0 4px',
    minWidth: '100px'
};

const BlockTypeDropdown = ({ disabled }: { disabled?: boolean }) => {
    const adapter = useEditorAdapter();
    const [currentBlockType, setCurrentBlockType] = useState<BlockTypeValue>(
        adapter.getCurrentBlockType()
    );

    useEffect(() => {
        return adapter.onBlockTypeChange(setCurrentBlockType);
    }, [adapter]);

    const current =
        BLOCK_TYPES.find((t) => t.value === currentBlockType) ?? BLOCK_TYPES[0];

    const handleSelect = (value: BlockTypeValue) => {
        adapter.applyBlockType(value);
    };

    return (
        <Menu shadow="md" width={160}>
            <Menu.Target>
                <UnstyledButton style={{ ...triggerStyle, opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : undefined }}>
                    {current.label}
                    <IconChevronDown size={14} />
                </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
                {BLOCK_TYPES.map((option) => (
                    <Menu.Item
                        key={option.value}
                        leftSection={option.icon}
                        onClick={() => handleSelect(option.value)}
                        bg={
                            option.value === currentBlockType
                                ? 'var(--mantine-color-gray-light)'
                                : undefined
                        }
                    >
                        {option.label}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
};

export default BlockTypeDropdown;
